import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

/**
 * Google Places (New) + Geocoding client.
 *
 * All requests go through the FastAPI backend so the Google API key
 * stays server-side. The returned "feature" shape mirrors the legacy
 * Photon geocoder we replaced, so onboarding UI consumers don't need
 * to change.
 *
 * Feature shape:
 * {
 *   id: string,                 // Google place_id
 *   title: string,
 *   subtitle: string,
 *   latitude: number,
 *   longitude: number,
 *   google_place_id: string,
 *   components: {
 *     country, country_code, region, city, postcode,
 *     street, house_number, name
 *   }
 * }
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api/onboarding`;

const makeSessionToken = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Simple fallback (RFC 4122 v4-ish; only used if crypto.randomUUID is absent).
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Autocomplete: fast, lightweight suggestions.
 * Returns [{ id, title, subtitle }] — coordinates are NOT fetched yet.
 * Call `fetchPlaceDetails(id, sessionToken)` when the user selects one.
 */
export async function autocompleteAddress(query, {
  sessionToken,
  language = 'en',
  lat,
  lon,
  signal,
} = {}) {
  const q = (query || '').trim();
  if (q.length < 2) return [];

  const body = { input: q, session_token: sessionToken, language };
  if (typeof lat === 'number' && typeof lon === 'number') {
    body.lat = lat;
    body.lng = lon;
  }
  const { data } = await axios.post(`${API}/places-autocomplete`, body, { signal });
  return Array.isArray(data?.candidates) ? data.candidates : [];
}

/**
 * Hydrate a place_id into the full normalized feature (lat/lng, components).
 * Must reuse the same session token that produced the suggestion so Google
 * charges this as one billable Autocomplete session, not two calls.
 */
export async function fetchPlaceDetails(placeId, { sessionToken, language = 'en' } = {}) {
  if (!placeId) return null;
  const { data } = await axios.post(`${API}/place-details`, {
    place_id: placeId,
    session_token: sessionToken,
    language,
  });
  return data || null;
}

/**
 * Forward-geocode a raw address string (e.g. AADE-supplied address).
 */
export async function geocodeAddress(address, { language = 'en' } = {}) {
  if (!address || address.trim().length < 2) return null;
  try {
    const { data } = await axios.post(`${API}/geocode`, {
      address: address.trim(),
      language,
    });
    return data || null;
  } catch (err) {
    if (err.response?.status === 404) return null;
    throw err;
  }
}

/**
 * Reverse-geocode a lat/lng pair. Used when the user drags the pin.
 */
export async function reverseGeocode(lat, lng, { language = 'en' } = {}) {
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  try {
    const { data } = await axios.post(`${API}/reverse-geocode`, {
      lat,
      lng,
      language,
    });
    return data || null;
  } catch (err) {
    if (err.response?.status === 404) return null;
    throw err;
  }
}

/**
 * React hook: debounced Google Places autocomplete.
 *
 * Returns { suggestions, loading, error, sessionToken, rotateSession }.
 * The consumer MUST call `rotateSession()` after firing the user's
 * final selection so the next search starts a fresh billing session.
 */
export function useGoogleAddressSearch(query, {
  delay = 260,
  language = 'en',
  lat,
  lon,
} = {}) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const controllerRef = useRef(null);
  const sessionRef = useRef(makeSessionToken());

  useEffect(() => {
    const q = (query || '').trim();
    if (q.length < 2) {
      setSuggestions([]);
      setLoading(false);
      setError(null);
      return undefined;
    }
    const timer = setTimeout(async () => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setLoading(true);
      setError(null);
      try {
        const results = await autocompleteAddress(q, {
          sessionToken: sessionRef.current,
          language,
          lat,
          lon,
          signal: controller.signal,
        });
        if (!controller.signal.aborted) setSuggestions(results);
      } catch (err) {
        if (!axios.isCancel?.(err) && err.name !== 'CanceledError' && err.name !== 'AbortError') {
          setError(err);
          setSuggestions([]);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [query, delay, language, lat, lon]);

  const rotateSession = () => {
    sessionRef.current = makeSessionToken();
  };

  return {
    suggestions,
    loading,
    error,
    sessionToken: sessionRef.current,
    rotateSession,
    getSessionToken: () => sessionRef.current,
  };
}
